<script lang="ts" generics="TCommitReceipt = undefined">
  import type { CTAOptions } from "@kontextwerk/web-sdk/ui/actions/cta/types"
  import { getAppointmentPlanningStartRecommendation } from "../../../../../api/kehrblatt/planning"
  import { resolveStartRecommendation } from "./_logic/startRecommendation"
  import {
    invalidatePreviewBeforeStart,
    previewRowMeetsStartBound,
    PLANNING_START_BOUND_MESSAGE,
    type AppointmentPlanningStartBound,
  } from "./_shared/_logic/start-bound"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { onMount, tick, untrack } from "svelte"
  import {
    AppointmentPlanningPreviewIntegrityError,
    listAppointmentPlanningManualFlexSlotOptions,
    type AppointmentPlanningPreviewPayload,
    type AppointmentPlanningPreviewResponse,
    type AppointmentPlanningRowResult,
    type PlanningUnitContactContext,
  } from "../../../../../api/kehrblatt/planning"
  import type {
    AppointmentPlanningExternalActionState,
    AppointmentPlanningExistingPlannedRow,
    AppointmentPlanningExistingSourceRow,
    AppointmentPlanningFlexMode,
    AppointmentPlanningOperations,
    AppointmentPlanningSettingsSnapshot,
    AppointmentPlanningSubmissionPhase,
    AppointmentPlanningSubmissionResult,
    AppointmentPlanningWorkOrderOverride,
    ManualFlexSlotOptionsState,
  } from "../../_logic/appointments-contract"
  import PlanningSettingsPanel from "./_modules/planning-settings-panel"
  import PreviewStatusMessages from "./_modules/preview-status-messages"
  import {
    createBatchPlanningPreviewController,
    type BatchPlanningPreviewRefreshOptions,
  } from "./_logic/previewController"
  import { formatDateOnly } from "../../_logic/dates"
  import {
    buildReuseAppointmentPlanningPreviewPayload,
    validateBatchPlanningRowLimit,
  } from "./_logic/payload"
  import {
    uniquePropertyCountForWorkOrders,
    validateDemoBatchPlanningPropertyLimit,
  } from "../../_logic/demo-batch-planning-limit"
  import AppointmentPlanningPreview from "./_modules/appointment-planning-preview"
  import type {
    PlanningPreviewRow,
    PlanningPreviewRowChange,
  } from "./_shared/_logic/table-contract"
  import { buildExistingWorkOrderPlanningPreviewRows } from "./_logic/tableModel"
  import {
    compactAppointmentPlanningOverride,
    compactAppointmentPlanningOverrides,
  } from "./_logic/rowOverrides"
  import {
    applyAppointmentPlanningSettings,
    appointmentPlanningFlexModeFromSettings,
    buildAppointmentPlanningSettingsSnapshot,
    buildInitialAppointmentPlanningSettings,
    employeeIdsFromOptions,
    employeeOptionsForWorkOrders,
    flexAppointmentPlanningModeForPayload,
    noKeptAppointmentPlanningSettings,
    normalizePendingSelectedEmployees,
    restrictEmployeeSelectionToOptions,
    selectedEmployeeValuesFromEvent,
    resolveSelectedEmployees,
  } from "./_logic/settingsModel"
  import {
    committedEventByWorkOrderId as buildCommittedEventByWorkOrderId,
    completedExistingPlanningRowIds,
    employeeLabelByIdFromOptions,
    filterExistingPlanningRows,
    isAppointmentPlanningPreviewReady,
    isAppointmentPlanningRowEditingDisabled,
    isAppointmentPlanningStepLocked,
    mergeExistingPlanningPreviewRows,
    planningEmployeeIdsForSelection,
    resolveAppointmentPlanningApplyTargets,
  } from "./_logic/surfaceModel"
  import { applyPlanningRowChange, buildPreviewRowsRemovalState } from "./_logic/rowChanges"
  import {
    planningStartDateForPreset,
    type PlanningStartPreset,
  } from "../../_logic/planning-start-preset"
  import { dateOnlyInTimezone } from "../../../../_shared/_logic/timezones"
  import { buildManualFlexSlotOptionsPayload } from "./_logic/manualFlexSlots"
  import {
    buildScopedPlanningPreviewPayload,
    mergeScopedPlanningPreview,
  } from "./_logic/previewRequest"
  import { applyCalendarAppointmentChange } from "./_logic/calendarChange"
  import type { AppointmentPlanningStateCommand } from "./_logic/contract"
  import {
    buildAppointmentPlanningCalendarSources,
    buildEffectiveAppointmentPlanningPreviewRows,
  } from "./_logic/previewProjection"
  import type { WorkingHoursByUser } from "../../../../../_shared/_logic/form-state"
  import CostBenefitNumbersBlock from "../cost-benefit-numbers-block"
  import AppointmentContactAvailabilityOverlay from "./_modules/appointment-contact-availability-overlay"
  import type { AppointmentPlanningCalendarAppointmentChange } from "./_shared/_logic/calendar-contract"
  import AppointmentPlanningSubmitAction from "./_modules/appointment-planning-submit-action"
  import { buildAppointmentPlanningSubmitIntent } from "./_actions/buildSubmitIntent"

  type Props = {
    organizationId: string
    trialRestrictionsActive?: boolean | null
    existingWorkOrderRows?: AppointmentPlanningExistingSourceRow[]
    unitContactContexts?: PlanningUnitContactContext[]
    initialSettings?: AppointmentPlanningSettingsSnapshot | null
    initialExistingPlannedRows?: AppointmentPlanningExistingPlannedRow[]
    hiddenRowIds?: string[]
    initialRemovedRowIds?: string[]
    durableReadOnlyRowIds?: string[]
    rowNumberById?: Record<string, number>
    orderTypes?: OrderType[]
    employeeOptions: Array<{ label: string; value: string }>
    workingHoursByUser?: WorkingHoursByUser
    flexSlotsPerDay?: number | null
    defaultStartDate: string
    defaultStartPreset: PlanningStartPreset
    settingsTimezone: string
    initialStartDateIsExplicit?: boolean
    operations: AppointmentPlanningOperations<TCommitReceipt>
    submitOptions?: CTAOptions
    externalActionState?: AppointmentPlanningExternalActionState
    existingSubmitLabel?: string | null
    tableTitle?: string | null
    tableDescription?: string | null
    helpModalTextId?: string
    readOnly?: boolean
    sourceLoading?: boolean
    sourceError?: string | null
    onSubmissionResult: (
      result: AppointmentPlanningSubmissionResult<TCommitReceipt>,
    ) => void | Promise<void>
    onRowsRemoved?: (rowIds: string[]) => void | Promise<void>
    onRowsRestored?: (rowIds: string[]) => void | Promise<void>
    onPreviewIntegrityError?: () => void
    setError?: (message: string | null) => void
  }

  const numberFormatter = new Intl.NumberFormat("de-DE")
  const {
    organizationId,
    trialRestrictionsActive = false,
    existingWorkOrderRows = [],
    unitContactContexts = [],
    initialSettings = null,
    initialExistingPlannedRows = [],
    hiddenRowIds = [],
    initialRemovedRowIds = [],
    durableReadOnlyRowIds = [],
    rowNumberById = {},
    orderTypes = [],
    employeeOptions,
    workingHoursByUser = {},
    flexSlotsPerDay = null,
    defaultStartDate,
    defaultStartPreset,
    settingsTimezone,
    initialStartDateIsExplicit = false,
    operations,
    submitOptions = undefined,
    externalActionState = undefined,
    existingSubmitLabel = null,
    tableTitle = null,
    tableDescription = null,
    helpModalTextId = "",
    readOnly = false,
    sourceLoading = false,
    sourceError = null,
    onSubmissionResult,
    onRowsRemoved = undefined,
    onRowsRestored = undefined,
    onPreviewIntegrityError = undefined,
    setError = () => {},
  }: Props = $props()

  const planningEmployeeOptions = $derived(
    employeeOptionsForWorkOrders(
      employeeOptions,
      existingWorkOrderRows.map((row) => row.workOrder),
    ),
  )
  const allEmployeeIds = () => employeeIdsFromOptions(planningEmployeeOptions)
  const activeEmployeeIdSet = () => new Set(allEmployeeIds())
  const restrictToActiveEmployees = (ids: unknown) =>
    restrictEmployeeSelectionToOptions(ids, allEmployeeIds())
  const sanitizeWorkOrderOverrides = (
    overrides: Record<string, AppointmentPlanningWorkOrderOverride>,
  ): Record<string, AppointmentPlanningWorkOrderOverride> => {
    const available = activeEmployeeIdSet()

    return compactAppointmentPlanningOverrides(
      Object.fromEntries(
        Object.entries(overrides).map(([rowId, override]) => {
          const next = { ...override }

          if (
            next.appointment_employee_id &&
            !available.has(String(next.appointment_employee_id))
          ) {
            delete next.appointment_employee_id
          }

          return [rowId, next]
        }),
      ),
    )
  }
  const initialFlexPlanningAvailable = untrack(() => Number(flexSlotsPerDay ?? 0) > 0)
  const initialSurfaceSettings: AppointmentPlanningSettingsSnapshot = untrack(() => {
    const employeeIds = allEmployeeIds()

    return buildInitialAppointmentPlanningSettings({
      initialSettings,
      employeeIds,
      defaultStartDate: initialStartDateIsExplicit
        ? defaultStartDate
        : (planningStartDateForPreset(defaultStartPreset, new Date(), settingsTimezone) ??
          defaultStartDate),
      defaultPlanFlexAppointments: initialFlexPlanningAvailable,
    })
  })
  const initialExistingPlannedRowsSnapshot = untrack(() => initialExistingPlannedRows)
  const initialRemovedRowIdsSnapshot = untrack(() =>
    Array.from(new Set(initialRemovedRowIds.map(String).filter(Boolean))),
  )
  const initialStartDate = formatDateOnly(initialSurfaceSettings.startDate)
  const initialFlexPlanningMode = appointmentPlanningFlexModeFromSettings({
    settings: initialSurfaceSettings,
    flexPlanningAvailable: initialFlexPlanningAvailable,
  })

  let workOrderOverridesByRowId = $state<Record<string, AppointmentPlanningWorkOrderOverride>>({
    ...compactAppointmentPlanningOverrides(initialSurfaceSettings.workOrderOverridesByRowId),
  })
  let dirtyRowIds = $state<Record<string, true>>({})
  let requiredPlanningRowIds = $state<Record<string, true>>({})
  let startDate = $state(initialStartDate)
  let pendingStartDate = $state(initialStartDate)
  const initialPreset = untrack(
    () =>
      initialSurfaceSettings.startPreset ??
      (initialSettings || initialStartDateIsExplicit ? "custom" : defaultStartPreset),
  )
  let startPreset = $state(initialPreset)
  let planningStartPreset = $state<PlanningStartPreset>(initialPreset)
  let recommendationTimezone = $state<string | null>(
    untrack(() => initialSurfaceSettings.startTimezone ?? null),
  )
  const startTimezone = $derived(recommendationTimezone ?? settingsTimezone)
  let startDateUserModified = $state(
    untrack(() => Boolean(initialSettings) || initialStartDateIsExplicit),
  )
  const startBound = $derived<AppointmentPlanningStartBound>({
    startDate: formatDateOnly(startDate),
    timezone: startTimezone,
  })
  let selectedEmployees = $state(
    resolveSelectedEmployees({
      value: initialSurfaceSettings.selectedEmployees,
      employeeIds: allEmployeeIds(),
    }),
  )
  let pendingSelectedEmployees = $state(
    resolveSelectedEmployees({
      value: initialSurfaceSettings.selectedEmployees,
      employeeIds: allEmployeeIds(),
    }),
  )
  let flexPlanningMode = $state<AppointmentPlanningFlexMode>(initialFlexPlanningMode)
  let pendingFlexPlanningMode = $state<AppointmentPlanningFlexMode>(initialFlexPlanningMode)
  // Planning settings are global and match the table, so the concrete values are preselected.
  let keptPlanningSettings = $state(noKeptAppointmentPlanningSettings())
  let previewResponse = $state<AppointmentPlanningPreviewResponse | null>(null)
  let previewLoading = $state(false)
  let previewPending = $state(false)
  let previewError = $state<string | null>(null)
  let submissionPhase = $state<AppointmentPlanningSubmissionPhase>("idle")
  let deletingRowId = $state<string | null>(null)
  let existingPlannedRows = $state([...initialExistingPlannedRowsSnapshot])
  let excludedRowIds = $state<string[]>([...initialRemovedRowIdsSnapshot])
  let applySelectedIds = $state<string[]>([])
  let manualFlexSlotOptionsByRowId = $state<Record<string, ManualFlexSlotOptionsState>>({})

  const flexPlanningOptions = [
    { label: "Erlauben", value: "plan" },
    { label: "Jeden zweiten freihalten", value: "every_second" },
    { label: "Nicht erlauben", value: "skip" },
  ]

  const externallyBlocked = $derived(
    Boolean(externalActionState?.blocksPlanning || externalActionState?.pending),
  )
  const planning = $derived(submissionPhase === "planning")
  const flexPlanningAvailable = $derived(Number(flexSlotsPerDay ?? 0) > 0)
  const sourceUnavailable = $derived(sourceLoading || Boolean(sourceError))
  const activeWorkOrderOverridesByRowId = $derived(
    sanitizeWorkOrderOverrides(workOrderOverridesByRowId),
  )
  const employeeLabelById = $derived(employeeLabelByIdFromOptions(employeeOptions))
  const hiddenRowIdSet = $derived(new Set(hiddenRowIds.map(String).filter(Boolean)))
  const excludedPlanningRowIds = $derived(Array.from(new Set([...hiddenRowIds, ...excludedRowIds])))
  const eligibleExistingRows = $derived(
    filterExistingPlanningRows(existingWorkOrderRows, excludedPlanningRowIds),
  )
  const committedExistingRowIds = $derived(
    new Set([
      ...completedExistingPlanningRowIds(existingPlannedRows),
      ...durableReadOnlyRowIds.map(String).filter(Boolean),
    ]),
  )
  const planningExistingRows = $derived(
    eligibleExistingRows.filter((row) => !committedExistingRowIds.has(row.id)),
  )
  const displayExistingRows = $derived(existingWorkOrderRows)
  const sourceRowCount = $derived(planningExistingRows.length)
  const planningPropertyCount = $derived.by(() => {
    return uniquePropertyCountForWorkOrders(planningExistingRows.map((row) => row.workOrder))
  })
  const dirtyRowIdSet = $derived(new Set(Object.keys(dirtyRowIds)))
  const previewDetailsStale = $derived(Object.keys(dirtyRowIds).length > 0)
  const requiredPlanningIds = $derived(new Set(Object.keys(requiredPlanningRowIds)))
  const previewRequirementsReady = $derived(
    !sourceUnavailable &&
      isAppointmentPlanningPreviewReady({
        sourceRowCount,
        startDate,
        selectedEmployees: restrictToActiveEmployees(selectedEmployees),
        existingRows: planningExistingRows,
        workOrderOverridesByRowId: activeWorkOrderOverridesByRowId,
      }),
  )
  const stepLocked = $derived(
    isAppointmentPlanningStepLocked({
      existingPlannedRows,
      existingRowIds: eligibleExistingRows.map((row) => row.id),
    }),
  )
  const committedEventByWorkOrderId = $derived(
    buildCommittedEventByWorkOrderId({ existingPlannedRows }),
  )

  const buildSettingsSnapshot = (): AppointmentPlanningSettingsSnapshot => ({
    ...buildAppointmentPlanningSettingsSnapshot({
      workOrderOverridesByRowId: activeWorkOrderOverridesByRowId,
      startDate,
      selectedEmployees: restrictToActiveEmployees(selectedEmployees),
      flexPlanningMode: flexPlanningAvailable ? flexPlanningMode : "skip",
    }),
    startPreset,
    startTimezone,
  })

  const planningEmployeeIdsForOverrides = (
    overrideMap: Record<string, AppointmentPlanningWorkOrderOverride>,
  ): string[] =>
    planningEmployeeIdsForSelection({
      selectedEmployees: restrictToActiveEmployees(selectedEmployees),
      workOrderOverridesByRowId: overrideMap,
    })

  const buildPlanningPayload = (
    overrideMap: Record<
      string,
      AppointmentPlanningWorkOrderOverride
    > = activeWorkOrderOverridesByRowId,
  ) => {
    const effectiveFlexPlanningMode = flexPlanningAvailable ? flexPlanningMode : "skip"
    const defaultFlexAppointmentAllowed = effectiveFlexPlanningMode !== "skip"
    const defaultFlexAppointmentMode =
      flexAppointmentPlanningModeForPayload(effectiveFlexPlanningMode)
    const employeeIds = planningEmployeeIdsForOverrides(overrideMap)

    return buildReuseAppointmentPlanningPreviewPayload({
      organizationId,
      rows: planningExistingRows,
      workOrderOverridesByRowId: overrideMap,
      employeeIds,
      startDate,
      defaultFlexAppointmentAllowed,
      defaultFlexAppointmentMode,
      flexPlanningAvailable,
    })
  }

  const previewController = createBatchPlanningPreviewController({
    isReady: () => previewRequirementsReady,
    buildPayload: () =>
      buildScopedPlanningPreviewPayload(
        buildPlanningPayload(),
        effectivePreviewRows,
        requiredPlanningIds,
      ),
    validatePayload: () =>
      validateDemoBatchPlanningPropertyLimit(planningPropertyCount, { trialRestrictionsActive }) ??
      validateBatchPlanningRowLimit(planningExistingRows.length),
    loadPreview: (payload, signal) => operations.loadPreview(payload, signal),
    getCurrentResponse: () => previewResponse,
    setResponse: (response) => {
      // Preserve the editable draft on failures; only a current successful response replaces targets.
      if (!response) return

      previewResponse = mergeScopedPlanningPreview(previewResponse, response, requiredPlanningIds)
      void tick().then(reconcileManualFlexSlotOptions)
    },
    setLoading: (loading) => {
      previewLoading = loading
    },
    setPending: (pending) => {
      previewPending = pending
    },
    setError: (message) => {
      previewError = message
    },
    onApplied: () => {
      // Context rows keep their draft, including the old route-advice snapshot.
      // Hide those advisory details after a scoped replan until a full preview refreshes them.
      dirtyRowIds = currentPlanningRowIds().every((id) => requiredPlanningIds.has(id))
        ? {}
        : Object.fromEntries(
            currentPlanningRowIds()
              .filter((id) => !requiredPlanningIds.has(id))
              .map((id) => [id, true as const]),
          )
      requiredPlanningRowIds = {}
    },
    onUnexpectedError: (error) => {
      console.error("appointment_planning_preview_failed", error)

      if (error instanceof AppointmentPlanningPreviewIntegrityError) {
        newNotification({ class: "error", html: error.message })
        onPreviewIntegrityError?.()
      }
    },
  })

  // The initial start is chosen once; a late recommendation must not restart planning.
  let initialPreviewStarted = false
  let initialStartTimer: ReturnType<typeof setTimeout> | null = null

  function queuePreviewRefresh(
    delay = 350,
    options: BatchPlanningPreviewRefreshOptions = {},
    rowIds: string[] = currentPlanningRowIds(),
  ) {
    if (externallyBlocked) return

    initialPreviewStarted = true

    if (initialStartTimer) clearTimeout(initialStartTimer)

    initialStartTimer = null
    const activeIds = new Set(currentPlanningRowIds())
    requiredPlanningRowIds = Object.fromEntries(
      [...Object.keys(requiredPlanningRowIds), ...rowIds]
        .filter((id) => activeIds.has(id))
        .map((id) => [id, true as const]),
    )

    if (!Object.keys(requiredPlanningRowIds).length) {
      previewController.cancel()
      previewError = null

      return
    }

    previewController.queue(delay, options)
  }

  const retryPlanning = () =>
    queuePreviewRefresh(0, { force: true }, Object.keys(requiredPlanningRowIds))

  const resumeRequiredPlanning = () => {
    previewController.cancel()
    previewError = null

    if (Object.keys(requiredPlanningRowIds).length) queuePreviewRefresh(350, { force: true }, [])
  }

  onMount(() => {
    if (!initialPreviewStarted) {
      if (startDateUserModified || readOnly || stepLocked || sourceUnavailable) {
        queuePreviewRefresh(0)
      } else {
        previewPending = true
        initialStartTimer = setTimeout(() => queuePreviewRefresh(0), 2000)
      }
    }

    return () => {
      if (initialStartTimer) clearTimeout(initialStartTimer)

      previewController.destroy()
    }
  })

  let standardStartCache: { key: string; startDate: string } | null = null

  $effect(() => {
    if (
      externallyBlocked ||
      startDateUserModified ||
      readOnly ||
      stepLocked ||
      planning ||
      sourceUnavailable
    )
      return

    const employeeIds = restrictToActiveEmployees(pendingSelectedEmployees).slice().sort()
    const organization = organizationId
    const preset = defaultStartPreset
    const timezone = settingsTimezone

    if (!organization) return

    const controller = new AbortController()
    const initialRecommendation = !initialPreviewStarted
    const selectionKey = employeeIds.join(",")
    const standardKey = JSON.stringify([
      organization,
      preset,
      timezone,
      dateOnlyInTimezone(new Date(), timezone),
    ])
    const current = () =>
      !externallyBlocked &&
      !controller.signal.aborted &&
      (!initialRecommendation || !initialPreviewStarted) &&
      !startDateUserModified &&
      !planning &&
      !stepLocked &&
      !readOnly &&
      !sourceUnavailable &&
      organization === organizationId &&
      preset === defaultStartPreset &&
      timezone === settingsTimezone &&
      selectionKey === restrictToActiveEmployees(pendingSelectedEmployees).slice().sort().join(",")
    const applyStart = (value: {
      startDate: string
      preset: PlanningStartPreset
      timezone: string
    }) => {
      if (!current()) return

      const changed = pendingStartDate !== value.startDate || startTimezone !== value.timezone
      pendingStartDate = value.startDate
      planningStartPreset = value.preset
      recommendationTimezone = value.timezone

      if (selectionKey === restrictToActiveEmployees(selectedEmployees).slice().sort().join(",")) {
        const appliedChanged = changed || startDate !== value.startDate
        startDate = value.startDate
        startPreset = value.preset

        if (appliedChanged && initialPreviewStarted) queuePreviewRefresh(0)
      }
    }
    // A recommendation belongs to one employee selection; only its standard fallback is reusable.
    untrack(() =>
      applyStart({
        startDate:
          standardStartCache?.key === standardKey
            ? standardStartCache.startDate
            : (planningStartDateForPreset(preset, new Date(), timezone) ?? defaultStartDate),
        preset,
        timezone,
      }),
    )
    const loadRecommendation = async () => {
      try {
        if (!employeeIds.length) return

        const response = await getAppointmentPlanningStartRecommendation(
          { organization_id: organization, employee_ids: employeeIds },
          { signal: controller.signal },
        )

        if (!current()) return

        const recommendation = resolveStartRecommendation(response, { defaultPreset: preset })

        if (!recommendation) return

        standardStartCache = { key: standardKey, startDate: response.default_start_date }
        applyStart(recommendation)
      } catch {
        // The context's standard fallback is already applied. Never replace explicit input.
      } finally {
        if (initialRecommendation && current()) queuePreviewRefresh(0)
      }
    }
    void loadRecommendation()

    return () => controller.abort()
  })

  const previewByRowId = $derived.by(() => {
    const rows = mergeExistingPlanningPreviewRows({
      previewRows: previewResponse?.rows ?? [],
      existingPlannedRows,
    })

    if (!previewDetailsStale) return rows

    return new Map(
      Array.from(rows, ([id, row]) => [
        id,
        {
          ...row,
          appointment: row.appointment
            ? {
                ...row.appointment,
                previous_anchor: null,
                next_anchor: null,
                customer_window_start_at: null,
                customer_window_end_at: null,
                customer_availability_match: null,
              }
            : row.appointment,
          warnings: row.warnings.filter((warning) => warning.code === "AFTER_DUE_DATE"),
        },
      ]),
    )
  })
  const calendarPreviewSources = $derived(
    buildAppointmentPlanningCalendarSources({
      planningExistingRows: eligibleExistingRows,
      orderTypes,
      routeProperties: previewResponse?.route_properties ?? [],
    }),
  )
  const previewRows: PlanningPreviewRow[] = $derived.by(() => {
    const rows = buildExistingWorkOrderPlanningPreviewRows({
      rows: displayExistingRows,
      previewByRowId,
      eventByWorkOrderId: committedEventByWorkOrderId,
      committedRowIds: committedExistingRowIds,
      workOrderOverridesByRowId: activeWorkOrderOverridesByRowId,
      employeeOptions: employeeOptions.map((option) => ({
        label: option.label,
        value: String(option.value),
      })),
      employeeLabelById,
      manualFlexSlotOptionsByRowId,
      unitContactContexts,
    })

    return rows.map((row) => ({
      ...row,
      removable: row.removable || Boolean(onRowsRemoved),
      removed: excludedRowIds.includes(row.id),
    }))
  })
  const activePreviewRows = $derived(
    previewRows.filter((row) => !row.removed && !hiddenRowIdSet.has(row.id)),
  )
  const activeEditablePreviewRows = $derived(activePreviewRows.filter((row) => !row.committed))
  const hasDurableOrRemovedPreviewRows = $derived(
    previewRows.some(
      (row) => !hiddenRowIdSet.has(row.id) && (row.committed || Boolean(row.removed)),
    ),
  )
  const effectivePreviewRows = $derived(
    buildEffectiveAppointmentPlanningPreviewRows({
      rows: activePreviewRows,
      previewByRowId,
      employeeLabelById,
      sourceEventIds: new Map(
        existingWorkOrderRows
          .filter((row) => row.workOrder.current_schedule_event_id !== undefined)
          .map((row) => [row.id, row.workOrder.current_schedule_event_id]),
      ),
    }),
  )
  const previewTitle = $derived(tableTitle || "Ergebnisvorschau")
  const boundedCalendarRows = $derived(
    invalidatePreviewBeforeStart(
      effectivePreviewRows.map((row) =>
        requiredPlanningIds.has(row.client_row_id)
          ? { ...row, appointment: null, commit_target: null, status: "unplanned" as const }
          : row,
      ),
      startBound,
    ),
  )
  const displayedPreviewRows = $derived.by(() => {
    const invalidIds = new Set(
      effectivePreviewRows
        .filter((row) => !previewRowMeetsStartBound(row, startBound))
        .map((row) => row.client_row_id),
    )

    return previewRows.map((row) =>
      requiredPlanningIds.has(row.id) && !row.committed
        ? {
            ...row,
            appointmentTag: {
              label: previewError ? "Planung fehlgeschlagen" : "Wird geplant…",
              color: previewError ? "red" : "neutral",
              tooltip: previewError ?? "Ein Terminvorschlag wird automatisch geladen.",
            },
          }
        : invalidIds.has(row.id) && !row.committed
          ? {
              ...row,
              appointmentTag: {
                label: "Neu planen",
                subLabel: "Vor Startdatum",
                color: "red",
                tooltip: PLANNING_START_BOUND_MESSAGE,
              },
              warningTag: {
                label: "Ja",
                subLabel: "Vor Startdatum",
                color: "red",
                tooltip: PLANNING_START_BOUND_MESSAGE,
              },
            }
          : row,
    )
  })
  const applyTargetResolution = $derived(
    resolveAppointmentPlanningApplyTargets({
      previewRows: activeEditablePreviewRows,
      selectedIds: applySelectedIds,
    }),
  )
  const applyTargetCount = $derived(applyTargetResolution.targetCount)
  const previewCount = $derived(
    previewRequirementsReady || stepLocked
      ? numberFormatter.format(activePreviewRows.length)
      : null,
  )
  const previewDescription = ""

  const rowEditingDisabled = $derived(
    externallyBlocked ||
      sourceUnavailable ||
      isAppointmentPlanningRowEditingDisabled({
        readOnly,
        stepLocked,
        planning,
        deletingRowId,
      }),
  )

  const markRowPlanningDirty = (rowId: string) => {
    dirtyRowIds = { ...dirtyRowIds, [rowId]: true }
    resumeRequiredPlanning()
    setError(null)
  }

  const markPlanningDirty = (nextDirtyRowIds: Record<string, true> = dirtyRowIds) => {
    dirtyRowIds = nextDirtyRowIds
    resumeRequiredPlanning()
    setError(null)
  }

  const updateRowOverride = (
    rowId: string,
    override: AppointmentPlanningWorkOrderOverride,
  ): Record<string, AppointmentPlanningWorkOrderOverride> => {
    const compacted = compactAppointmentPlanningOverride(override)
    const nextOverrides = { ...workOrderOverridesByRowId }

    if (compacted) {
      nextOverrides[rowId] = compacted
    } else {
      delete nextOverrides[rowId]
    }

    workOrderOverridesByRowId = nextOverrides
    markRowPlanningDirty(rowId)

    return nextOverrides
  }

  const resetManualFlexSlotOptions = (rowId: string) => {
    manualFlexSlotOptionsByRowId = { ...manualFlexSlotOptionsByRowId, [rowId]: { status: "idle" } }
  }

  const rowWithOverrideForFlexSlotQuery = (
    row: PlanningPreviewRow,
    override: AppointmentPlanningWorkOrderOverride | undefined,
  ): PlanningPreviewRow => ({
    ...row,
    planningStrategy: override?.planning_strategy ?? row.planningStrategy,
    appointmentTypeValue: override?.manual_appointment_type ?? row.appointmentTypeValue,
    appointmentEmployeeId: String(
      override?.appointment_employee_id ?? row.appointmentEmployeeId ?? "",
    ),
    estimatedDurationValue: String(row.estimatedDurationValue ?? ""),
    manualFlexDate: String(override?.manual_flex_date ?? row.manualFlexDate ?? ""),
  })

  const loadManualFlexSlots = async (
    row: PlanningPreviewRow,
    overrides: Record<string, AppointmentPlanningWorkOrderOverride>,
    planningRequest: AppointmentPlanningPreviewPayload | null = buildPlanningPayload(overrides),
  ) => {
    const effectiveRow = rowWithOverrideForFlexSlotQuery(row, overrides[row.id])
    const payloadInfo = buildManualFlexSlotOptionsPayload({
      organizationId,
      planningRequest,
      row: effectiveRow,
    })

    if (!payloadInfo) {
      resetManualFlexSlotOptions(row.id)

      return
    }

    const { payload, queryKey } = payloadInfo
    manualFlexSlotOptionsByRowId = {
      ...manualFlexSlotOptionsByRowId,
      [row.id]: { status: "loading", queryKey },
    }
    try {
      const response = await listAppointmentPlanningManualFlexSlotOptions(payload)
      const currentState = manualFlexSlotOptionsByRowId[row.id]

      if (currentState?.status !== "loading" || currentState.queryKey !== queryKey) return

      manualFlexSlotOptionsByRowId = {
        ...manualFlexSlotOptionsByRowId,
        [row.id]: { status: "ready", queryKey, options: response.options ?? [] },
      }
    } catch (error) {
      console.error("appointment_planning_manual_flex_slots_failed", error)
      const currentState = manualFlexSlotOptionsByRowId[row.id]

      if (currentState?.status !== "loading" || currentState.queryKey !== queryKey) return

      manualFlexSlotOptionsByRowId = {
        ...manualFlexSlotOptionsByRowId,
        [row.id]: {
          status: "error",
          queryKey,
          message: "Flextermine konnten nicht geladen werden.",
        },
      }
    }
  }

  const reconcileManualFlexSlotOptions = () => {
    if (externallyBlocked || !previewRequirementsReady) return

    const overrides = activeWorkOrderOverridesByRowId
    const planningRequest = buildPlanningPayload(overrides)

    if (!planningRequest) return

    for (const row of activePreviewRows) {
      if (row.committed) continue

      const effectiveRow = rowWithOverrideForFlexSlotQuery(row, overrides[row.id])
      const payloadInfo = buildManualFlexSlotOptionsPayload({
        organizationId,
        planningRequest,
        row: effectiveRow,
      })

      if (!payloadInfo) continue

      const currentState = manualFlexSlotOptionsByRowId[row.id]

      if (
        currentState &&
        currentState.status !== "idle" &&
        currentState.queryKey === payloadInfo.queryKey
      ) {
        continue
      }

      void loadManualFlexSlots(row, overrides, planningRequest)
    }
  }

  const runPlanningStateCommands = (
    commands: AppointmentPlanningStateCommand[],
    overrides: Record<string, AppointmentPlanningWorkOrderOverride> = workOrderOverridesByRowId,
  ) => {
    for (const command of commands) {
      if (command.kind === "mark_row_planning_dirty") {
        markRowPlanningDirty(command.rowId)
        continue
      }

      if (command.kind === "load_manual_flex_slots") {
        const commandRow = previewRows.find((item) => item.id === command.rowId)

        if (commandRow) void loadManualFlexSlots(commandRow, overrides)
      }
    }
  }

  const handlePreviewRowChange = (row: PlanningPreviewRow, change: PlanningPreviewRowChange) => {
    if (rowEditingDisabled || row.committed) return

    if (change.kind === "retry_manual_flex_slots") {
      void loadManualFlexSlots(row, activeWorkOrderOverridesByRowId)

      return
    }

    const current = workOrderOverridesByRowId[row.id] ?? {}
    const result = applyPlanningRowChange({
      startBound,
      row,
      change,
      current,
    })

    if (result.error) {
      setError(result.error)

      return
    }

    resetManualFlexSlotOptions(row.id)

    if (!result.override) return

    const nextOverrides = updateRowOverride(row.id, result.override)
    runPlanningStateCommands(result.commands, nextOverrides)
    const needsProposal =
      (change.kind === "planning_strategy" && change.value === "AUTO") ||
      (change.kind === "appointment_employee" && !row.appointmentStartAt)

    if (needsProposal) queuePreviewRefresh(0, { force: true }, [row.id])
    else if (result.override.manual_appointment && previewResponse) {
      const { [row.id]: _completed, ...remaining } = requiredPlanningRowIds
      requiredPlanningRowIds = remaining
      resumeRequiredPlanning()
    }
  }

  const preferredAppointmentContactCustomerId = (row: PlanningPreviewRow): string | null => {
    const overrideCustomerId = String(
      workOrderOverridesByRowId[row.id]?.appointment_contact_customer_id ?? "",
    ).trim()

    if (overrideCustomerId) return overrideCustomerId

    const resolvedCustomerId = String(row.resolvedAppointmentContact?.customer_id ?? "").trim()

    return resolvedCustomerId || null
  }

  const handleContactAvailabilitySaved = (
    row: PlanningPreviewRow,
    detail: { customerId: string },
  ) => {
    if (rowEditingDisabled || row.committed) return

    const customerId = String(detail.customerId ?? "").trim()

    if (!customerId) return

    const target = effectivePreviewRows.find(
      (entry) => entry.client_row_id === row.id,
    )?.commit_target
    updateRowOverride(row.id, {
      ...(workOrderOverridesByRowId[row.id] ?? {}),
      appointment_contact_customer_id: customerId,
      ...(target?.kind === "SET"
        ? { planning_strategy: "MANUAL", manual_appointment: target.appointment }
        : {}),
    })
    queuePreviewRefresh(0, { force: true }, [row.id])
  }

  const removePreviewRows = (rows: PlanningPreviewRow[]) => {
    if (stepLocked || readOnly || rowEditingDisabled) return

    const removableRows = rows.filter(
      (row) =>
        Boolean(row?.id) &&
        row.removable &&
        !row.committed &&
        !row.removed &&
        !excludedRowIds.includes(row.id),
    )

    if (!removableRows.length) return

    const nextState = buildPreviewRowsRemovalState({
      rows: removableRows,
      workOrderOverridesByRowId,
      excludedRowIds,
      dirtyRowIds,
    })
    workOrderOverridesByRowId = nextState.workOrderOverridesByRowId
    excludedRowIds = nextState.excludedRowIds
    selectedEmployees = restrictToActiveEmployees(selectedEmployees)
    requiredPlanningRowIds = Object.fromEntries(
      Object.entries(requiredPlanningRowIds).filter(([id]) => !excludedRowIds.includes(id)),
    )
    markPlanningDirty({
      ...nextState.dirtyRowIds,
      ...Object.fromEntries(removableRows.map((row) => [row.id, true as const])),
    })
    newNotification({
      class: "success",
      html:
        removableRows.length === 1
          ? "Zeile aus diesem Schritt entfernt."
          : `${numberFormatter.format(removableRows.length)} Zeilen aus diesem Schritt entfernt.`,
    })
  }

  const removePreviewRow = (row: PlanningPreviewRow) => removePreviewRows([row])

  const restorePreviewRow = async (row: PlanningPreviewRow) => {
    if (rowEditingDisabled || stepLocked || readOnly || !row?.id) return

    if (!excludedRowIds.includes(row.id)) return

    await onRowsRestored?.([row.id])

    if (rowEditingDisabled) return

    excludedRowIds = excludedRowIds.filter((id) => id !== row.id)
    markPlanningDirty({ ...dirtyRowIds, [row.id]: true })
    queuePreviewRefresh(0, { force: true }, [row.id])
  }

  const resetPreviewRows = (rows: PlanningPreviewRow[]) => {
    if (stepLocked || readOnly || rowEditingDisabled) return

    const resettableRows = rows.filter((row) => Boolean(row?.id) && !row.committed && !row.removed)

    if (!resettableRows.length) return

    const nextOverrides = { ...workOrderOverridesByRowId }
    const nextManualFlexSlots = { ...manualFlexSlotOptionsByRowId }
    const nextDirtyRowIds = { ...dirtyRowIds }
    for (const row of resettableRows) {
      delete nextOverrides[row.id]
      nextManualFlexSlots[row.id] = { status: "idle" }
      nextDirtyRowIds[row.id] = true
    }
    workOrderOverridesByRowId = nextOverrides
    manualFlexSlotOptionsByRowId = nextManualFlexSlots
    markPlanningDirty(nextDirtyRowIds)
    queuePreviewRefresh(
      0,
      { force: true },
      resettableRows.map((row) => row.id),
    )
  }

  const resetPreviewRow = (row: PlanningPreviewRow) => resetPreviewRows([row])

  const submitIntent = $derived.by(() =>
    buildAppointmentPlanningSubmitIntent({
      startBound,
      existingSubmitLabel,
      sourceLoading,
      sourceError,
      stepLocked,
      rowCount: planningExistingRows.length,
      planningPropertyCount,
      trialRestrictionsActive,
      startDate,
      selectedEmployees: restrictToActiveEmployees(selectedEmployees),
      previewLoading: previewLoading || previewPending,
      previewCurrent: Boolean(previewResponse) && requiredPlanningIds.size === 0,
      previewError,
      previewPayload: buildPlanningPayload(),
      effectivePreviewRows,
      settings: buildSettingsSnapshot(),
      existingPlannedRows,
      planningRows: planningExistingRows,
      previewByRowId,
      removedRowIds: excludedRowIds,
    }),
  )

  const handleSubmissionResult = async (
    submission: AppointmentPlanningSubmissionResult<TCommitReceipt>,
  ) => {
    if (submission.kind === "removed") {
      if (submission.removedRowIds.length) await onRowsRemoved?.(submission.removedRowIds)
    } else if (submission.kind === "committed") {
      const result = submission.result

      existingPlannedRows = result.plannedRows

      if (submission.removedRowIds.length) await onRowsRemoved?.(submission.removedRowIds)
    }

    await onSubmissionResult(submission)
  }

  const normalizeCurrentPendingSelectedEmployees = (value: string[]) =>
    normalizePendingSelectedEmployees({
      value,
      allEmployeeIds: allEmployeeIds(),
    })

  const currentPlanningRowIds = () => planningExistingRows.map((row) => row.id)

  const currentApplyTargetIds = () => {
    return applyTargetResolution.targetRows.map((row) => row.id)
  }

  const applyPlanningSettings = async () => {
    if (externallyBlocked || stepLocked || readOnly || planning || deletingRowId) return

    const applied = applyAppointmentPlanningSettings({
      rowIds: currentPlanningRowIds(),
      targetIds: currentApplyTargetIds(),
      applySelectedIds,
      keptSettings: keptPlanningSettings,
      currentStartDate: startDate,
      pendingStartDate,
      currentSelectedEmployees: selectedEmployees,
      pendingSelectedEmployees,
      currentFlexPlanningMode: flexPlanningMode,
      pendingFlexPlanningMode,
      flexPlanningAvailable,
      workOrderOverridesByRowId: activeWorkOrderOverridesByRowId,
      allEmployeeIds: allEmployeeIds(),
    })

    workOrderOverridesByRowId = applied.workOrderOverridesByRowId
    startDate = applied.startDate

    if (!keptPlanningSettings.startDate) startPreset = planningStartPreset

    selectedEmployees = applied.selectedEmployees
    flexPlanningMode = applied.flexPlanningMode
    queuePreviewRefresh(0, { force: true }, currentApplyTargetIds())
  }

  const planningSettingsHandlers = {
    syncStartDate: (change: { startDate: string; preset: PlanningStartPreset }) => {
      if (externallyBlocked || stepLocked || readOnly || planning) return

      startDateUserModified = true
      pendingStartDate = formatDateOnly(change.startDate)
      planningStartPreset = change.preset

      if (!initialPreviewStarted) {
        startDate = pendingStartDate
        startPreset = planningStartPreset
        queuePreviewRefresh(0)
      }
    },
    syncSelectedEmployees: (event?: Event) => {
      if (externallyBlocked || stepLocked || readOnly || planning) return

      const nextSelected = selectedEmployeeValuesFromEvent(event) ?? pendingSelectedEmployees
      pendingSelectedEmployees = normalizeCurrentPendingSelectedEmployees(nextSelected)
    },
    syncFlexPlanningMode: () => {
      if (externallyBlocked || stepLocked || readOnly || planning) return

      pendingFlexPlanningMode =
        flexPlanningAvailable &&
        (pendingFlexPlanningMode === "plan" || pendingFlexPlanningMode === "every_second")
          ? pendingFlexPlanningMode
          : "skip"
    },
  }

  const planningSettingsDisabledState = $derived({
    stepLocked: externallyBlocked || stepLocked || readOnly || sourceUnavailable,
    planning,
    hasEmployeeOptions: Boolean(planningEmployeeOptions.length),
    hasRotatingEmployeeOptions: Boolean(planningEmployeeOptions.length),
  })

  const openContactAvailabilityOverlay = (row: PlanningPreviewRow) => {
    if (rowEditingDisabled || row.committed) return

    if (!organizationId || !row.propertyId) {
      newNotification({
        class: "error",
        html: "Für diese Zeile kann keine Ansprechpartner-Verfügbarkeit geöffnet werden.",
      })

      return
    }

    openOverlay({
      title: "Ansprechpartner-Verfügbarkeit bearbeiten",
      content: AppointmentContactAvailabilityOverlay,
      isDark: false,
      closeOnParentOpen: false,
      properties: {
        organizationId,
        row,
        initialCustomerId: preferredAppointmentContactCustomerId(row),
        onSaved: (detail: { customerId: string }) => handleContactAvailabilitySaved(row, detail),
      },
    })
  }

  const handleCalendarAppointmentChange = (
    detail: AppointmentPlanningCalendarAppointmentChange,
  ) => {
    if (committedExistingRowIds.has(detail.clientRowId)) return

    const result = applyCalendarAppointmentChange({
      startBound,
      blocked: externallyBlocked || stepLocked || readOnly || planning,
      previewResponse,
      workOrderOverridesByRowId,
      detail,
    })

    if (result.kind === "ignored") {
      if (result.reason === "before_start") setError(PLANNING_START_BOUND_MESSAGE)

      return
    }

    previewResponse = result.previewResponse
    workOrderOverridesByRowId = result.workOrderOverridesByRowId
    const { [detail.clientRowId]: _completed, ...remaining } = requiredPlanningRowIds
    requiredPlanningRowIds = remaining
    runPlanningStateCommands(result.commands, result.workOrderOverridesByRowId)
  }
</script>

<div class="wrapper">
  <AppointmentPlanningSubmitAction
    intent={submitIntent}
    {submitOptions}
    {externalActionState}
    {operations}
    onSubmissionResult={handleSubmissionResult}
    onPhaseChange={(phase) => {
      submissionPhase = phase
    }}
  >
    {#snippet children(primaryAction)}
      <AppointmentPlanningPreview
        {primaryAction}
        title={previewTitle}
        count={previewCount}
        {rowNumberById}
        description={previewDescription}
        showPreview={sourceUnavailable ||
          previewRequirementsReady ||
          hasDurableOrRemovedPreviewRows}
        loading={sourceLoading || previewPending || previewLoading}
        tableError={sourceError}
        rows={displayedPreviewRows}
        {startBound}
        {hiddenRowIds}
        {deletingRowId}
        disabled={rowEditingDisabled}
        onRowChange={handlePreviewRowChange}
        onRemove={removePreviewRow}
        onRemoveRows={onRowsRemoved ? removePreviewRows : undefined}
        onRestore={restorePreviewRow}
        onResetRow={resetPreviewRow}
        onResetRows={resetPreviewRows}
        onEditContactAvailability={rowEditingDisabled ? undefined : openContactAvailabilityOverlay}
        {organizationId}
        calendarRows={boundedCalendarRows}
        calendarSources={calendarPreviewSources}
        employeeOptions={employeeOptions.map((option) => ({
          label: option.label,
          value: String(option.value),
        }))}
        {employeeLabelById}
        {workingHoursByUser}
        allowCalendarAppointmentResize={!readOnly && !externallyBlocked}
        readOnlyRowIds={[...committedExistingRowIds]}
        onCalendarAppointmentChange={handleCalendarAppointmentChange}
        showMetrics={sourceUnavailable ||
          previewRequirementsReady ||
          Boolean(previewResponse?.cost_benefit)}
        showFooter={!readOnly}
        {helpModalTextId}
        enableSelection={!readOnly && !externallyBlocked}
        onApplyScopeChange={(detail) => {
          applySelectedIds = detail.selectedIds
        }}
      >
        {#snippet preconfiguration()}
          <PlanningSettingsPanel
            bind:startDate={pendingStartDate}
            bind:planningStartPreset
            planningTimezone={startTimezone}
            bind:selectedEmployees={
              () => restrictToActiveEmployees(pendingSelectedEmployees),
              (next) => {
                pendingSelectedEmployees = next
              }
            }
            bind:flexPlanningMode={pendingFlexPlanningMode}
            bind:keptSettings={keptPlanningSettings}
            {flexPlanningAvailable}
            employeeOptions={planningEmployeeOptions}
            {flexPlanningOptions}
            disabledState={planningSettingsDisabledState}
            handlers={planningSettingsHandlers}
            applySelectedCount={applySelectedIds.length}
            {applyTargetCount}
            applyPending={sourceLoading || previewPending || previewLoading}
            applyDisabled={externallyBlocked ||
              sourceUnavailable ||
              readOnly ||
              stepLocked ||
              planning ||
              Boolean(deletingRowId) ||
              applyTargetCount === 0}
            onApply={applyPlanningSettings}
          />
        {/snippet}

        {#snippet metrics()}
          <CostBenefitNumbersBlock
            estimate={previewDetailsStale ? null : (previewResponse?.cost_benefit ?? null)}
            loading={sourceLoading ||
              ((previewPending || previewLoading) && previewRequirementsReady)}
            darkMode={true}
          />
          <PreviewStatusMessages
            show={previewRequirementsReady}
            {previewError}
            retryPending={previewPending || previewLoading}
            onRetry={!externallyBlocked && requiredPlanningIds.size ? retryPlanning : undefined}
            optimizationWarning={previewDetailsStale
              ? null
              : (previewResponse?.optimization_warning ?? null)}
          />
        {/snippet}

        {#snippet footer()}
          <div class="appointment-planning-footer-actions">
            {@render primaryAction()}
          </div>
        {/snippet}
      </AppointmentPlanningPreview>
    {/snippet}
  </AppointmentPlanningSubmitAction>
</div>

<style lang="less">
  .wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .appointment-planning-footer-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.65rem;
    flex-wrap: wrap;
    width: 100%;
  }
</style>
